/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import "./react-select.scss";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { useLoggerStore } from "../../lib/store-logger";
import Logger, { LoggerFilterType } from "../logger/Logger";
import "./side-panel.scss";

const filterOptions = [
  { value: "conversations", label: "Conversations" },
  { value: "tools", label: "Tool Use" },
  { value: "none", label: "All" },
];

const navItems = [
  { path: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
  { path: '/learn', icon: 'school', label: 'Learn' },
  { path: '/certify', icon: 'verified', label: 'Certify' },
  { path: '/jobs', icon: 'work', label: 'Jobs' },
  { path: '/connect', icon: 'groups', label: 'Connect' },
  { path: '/wallet', icon: 'account_balance_wallet', label: 'Wallet' },
  { path: '/portfolio', icon: 'person', label: 'Portfolio' },
  { path: '/cv-generator', icon: 'description', label: 'CV Generator' },
  { path: '/career-guidance', icon: 'psychology', label: 'Career Guide' },
];

export default function SidePanel() {
  const { connected, client } = useLiveAPIContext();
  const [open, setOpen] = useState(true);
  const [showNav, setShowNav] = useState(false);
  const loggerRef = useRef<HTMLDivElement>(null);
  const loggerLastHeightRef = useRef<number>(-1);
  const { log, logs } = useLoggerStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [textInput, setTextInput] = useState("");
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  //scroll the log to the bottom when new logs come in
  useEffect(() => {
    if (loggerRef.current) {
      const el = loggerRef.current;
      const scrollHeight = el.scrollHeight;
      if (scrollHeight !== loggerLastHeightRef.current) {
        el.scrollTop = scrollHeight;
        loggerLastHeightRef.current = scrollHeight;
      }
    }
  }, [logs]);

  // listen for log events and store them
  useEffect(() => {
    client.on("log", log);
    return () => {
      client.off("log", log);
    };
  }, [client, log]);

  const handleSubmit = () => {
    client.send([{ text: textInput }]);

    setTextInput("");
    if (inputRef.current) {
      inputRef.current.innerText = "";
    }
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setShowNav(false);
  };

  return (
    <div className={`side-panel ${open ? "open" : ""}`}>
      <header className="top">
        <div className="header-left">
          <button className="nav-toggle" onClick={() => setShowNav(!showNav)}>
            <span className="material-symbols-outlined">{showNav ? 'close' : 'apps'}</span>
          </button>
          <h2>Console</h2>
        </div>
        {open ? (
          <button className="opener" onClick={() => setOpen(false)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        ) : (
          <button className="opener" onClick={() => setOpen(true)}>
            <span className="material-symbols-outlined">menu</span>
          </button>
        )}
      </header>

      {/* App Navigation Drawer */}
      {showNav && (
        <div className="app-nav-drawer">
          <div className="nav-header">
            <h3>Navigate to</h3>
          </div>
          <nav className="app-nav">
            {navItems.map((item) => (
              <button
                key={item.path}
                className={cn('nav-item', { active: location.pathname === item.path })}
                onClick={() => handleNavClick(item.path)}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
            <div className="nav-divider"></div>
            <button
              className="nav-item"
              onClick={() => handleNavClick('/client')}
            >
              <span className="material-symbols-outlined">business</span>
              <span>Client Portal</span>
            </button>
            <button
              className="nav-item home"
              onClick={() => handleNavClick('/')}
            >
              <span className="material-symbols-outlined">home</span>
              <span>Back to Home</span>
            </button>
          </nav>
        </div>
      )}

      <section className="indicators">
        <Select
          className="react-select"
          classNamePrefix="react-select"
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              background: "var(--Neutral-15)",
              color: "var(--Neutral-90)",
              minHeight: "33px",
              maxHeight: "33px",
              border: 0,
            }),
            option: (styles, { isFocused, isSelected }) => ({
              ...styles,
              backgroundColor: isFocused
                ? "var(--Neutral-30)"
                : isSelected
                  ? "var(--Neutral-20)"
                  : undefined,
            }),
          }}
          defaultValue={selectedOption}
          options={filterOptions}
          onChange={(e) => {
            setSelectedOption(e);
          }}
        />
        <div className={cn("streaming-indicator", { connected })}>
          {connected
            ? `🔵${open ? " Streaming" : ""}`
            : `⏸️${open ? " Paused" : ""}`}
        </div>
      </section>
      <div className="side-panel-container" ref={loggerRef}>
        <Logger
          filter={(selectedOption?.value as LoggerFilterType) || "none"}
        />
      </div>
      <div className={cn("input-container", { disabled: !connected })}>
        <div className="input-content">
          <textarea
            className="input-area"
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }
            }}
            onChange={(e) => setTextInput(e.target.value)}
            value={textInput}
          ></textarea>
          <span
            className={cn("input-content-placeholder", {
              hidden: textInput.length,
            })}
          >
            Type&nbsp;something...
          </span>

          <button
            className="send-button material-symbols-outlined filled"
            onClick={handleSubmit}
          >
            send
          </button>
        </div>
      </div>
    </div>
  );
}
